// Android コンパニオンアプリ（localhost:7777）経由でアルコール検査を行う
// CapacitorHttp（ネイティブHTTP）経由で通信し、WebViewのCORS/PNA/混在コンテンツ制約を迂回する
import { Capacitor, CapacitorHttp } from '@capacitor/core';

const COMPANION_BASE = 'http://localhost:7777';
const useNative = Capacitor.isNativePlatform();

/** localhost:7777 へGET。ネイティブ(Capacitor)ではCapacitorHttp、それ以外はfetch */
async function companionGet(path: string): Promise<{ status: number; text: string }> {
  if (useNative) {
    const r = await CapacitorHttp.get({ url: `${COMPANION_BASE}${path}`, headers: { 'Cache-Control': 'no-store' }, readTimeout: 6000, connectTimeout: 6000 });
    const text = typeof r.data === 'string' ? r.data : JSON.stringify(r.data);
    return { status: r.status, text };
  }
  const res = await fetch(`${COMPANION_BASE}${path}`, { cache: 'no-store' });
  return { status: res.status, text: await res.text() };
}

export async function isCompanionAvailable(): Promise<boolean> {
  return (await companionPing()).ok;
}

/**
 * コンパニオン(localhost:7777)にPDFのURLを渡し、端末のAndroid印刷（ネットワークプリンター）へ送る。
 * ブラウザ非搭載のキオスク端末でも印刷できる。成功で true。
 */
export async function companionPrint(pdfUrl: string, name: string): Promise<boolean> {
  try {
    if (useNative) {
      const r = await CapacitorHttp.post({
        url: `${COMPANION_BASE}/print`,
        headers: { 'Content-Type': 'application/json' },
        data: { url: pdfUrl, name },
        readTimeout: 30000, connectTimeout: 6000,
      });
      return r.status >= 200 && r.status < 300;
    }
    const res = await fetch(`${COMPANION_BASE}/print`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: pdfUrl, name }),
    });
    return res.ok;
  } catch { return false; }
}

/** ping結果と失敗理由を返す（画面診断用） */
export async function companionPing(): Promise<{ ok: boolean; detail: string }> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const r = await companionGet('/ping');
      if (r.status === 200) return { ok: true, detail: `ping OK${useNative ? '(native)' : ''}` };
      return { ok: false, detail: `HTTP ${r.status}` };
    } catch (e: any) {
      if (attempt === 2) return { ok: false, detail: `接続不可: ${e?.message ?? String(e)}` };
    }
  }
  return { ok: false, detail: '接続不可' };
}

export type CompanionEvent =
  | { status: 'warming';  countdown: number }
  | { status: 'waiting' }
  | { status: 'done';    alcohol_value: number }
  | { status: 'timeout' }
  | { status: 'raw';     hex: string }
  | { status: 'error';   message: string };

/** EventSource の代わりに使う「停止可能なポーリング」オブジェクト */
export interface CompanionPoller {
  close(): void;
}

export function startCompanionCheck(
  onWarming:  (countdown: number) => void,
  onWaiting:  () => void,
  onDone:     (value: number) => void,
  onTimeout:  () => void,
  onError:    (msg: string) => void,
  onRaw?:     (hex: string) => void,
): CompanionPoller {
  let stopped = false;

  // まず測定を開始する
  companionGet('/measure-start').catch(() => {
    if (!stopped) {
      onError('測定開始に失敗しました');
      stopped = true;
    }
  });

  // 600ms ごとにステータスをポーリング
  const poll = async () => {
    if (stopped) return;

    try {
      const r = await companionGet('/measure-status');
      const data: CompanionEvent = JSON.parse(r.text);

      if (stopped) return;

      switch (data.status) {
        case 'warming':
          onWarming(data.countdown);
          break;
        case 'waiting':
          onWaiting();
          break;
        case 'done':
          onDone(data.alcohol_value);
          stopped = true;
          return;
        case 'timeout':
          onTimeout();
          stopped = true;
          return;
        case 'raw':
          onRaw?.(data.hex);
          break;
        case 'error':
          onError(data.message);
          stopped = true;
          return;
      }
    } catch {
      if (!stopped) {
        onError('開発者専用アプリと通信できませんでした。端末を再起動してから、もう一度お試しください');
        stopped = true;
        return;
      }
    }

    // 次のポーリングをスケジュール
    setTimeout(poll, 600);
  };

  // 200ms後にポーリング開始（measure-startが先に届くように）
  setTimeout(poll, 200);

  return { close: () => { stopped = true; } };
}

/** プリンター診断の1項目。status: ok=問題なし / warn=要確認 / ng=このままでは不可 / skip=省略 */
export interface PrinterCheck { key: string; label: string; status: 'ok' | 'warn' | 'ng' | 'skip'; detail: string; hint: string }
export interface PrinterDiagnosis {
  ok: boolean; overall: 'ok' | 'warn' | 'ng'; summary: string; ip: string; device_ip: string | null;
  found: { name: string; host: string; port: number; type: string }[];
  checks: PrinterCheck[];
}

/**
 * コンパニオン(localhost:7777)に船内プリンターの診断を頼む（Wi-Fi→探索→到達→本体状態→印刷サービス）。
 * 探索を含むため10秒前後かかる。コンパニオンが無い端末（個人スマホ・ブラウザ）では null を返す。
 */
export async function companionPrinterCheck(ip: string): Promise<PrinterDiagnosis | null> {
  const path = `/printer-check?ip=${encodeURIComponent(ip)}`;
  try {
    if (useNative) {
      const r = await CapacitorHttp.get({ url: `${COMPANION_BASE}${path}`, headers: { 'Cache-Control': 'no-store' }, readTimeout: 30000, connectTimeout: 4000 });
      const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
      return r.status === 200 && data?.ok ? data as PrinterDiagnosis : null;
    }
    const res = await fetch(`${COMPANION_BASE}${path}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.ok ? data as PrinterDiagnosis : null;
  } catch {
    return null;
  }
}
